import { useMemo, useState } from "react";
import { Anchor, Building2, Check, Gem, MapPin, Search } from "lucide-react";
import { Face } from "@/city3d/Hud";
import { pointOf } from "./CoinFly";
import { Btn, Info } from "./kit";
import { ACC, fmt, tradeBlock } from "./util";

const VIEWS = [["week", "Week", "wk"], ["all", "All time", "e"]];
const SHOWN = 50;

// Lit houses, best first by `key` ("wk" for the week, "e" for all time).
export const ladder = (residents, key) => residents.filter((r) => r.s === "f" && !r.out)
  .sort((a, b) => b[key] - a[key] || (a.k < b.k ? -1 : 1)).map((r, i) => ({ ...r, rank: i + 1 }));

function Row({ r, i, view, why, mine, onFly, onTrade }) {
  const row = view === "week" && r.rank <= 10, Where = row ? Gem : r.u ? Building2 : Anchor;
  return (
    <li data-testid="leaderboard-row" data-handle={r.h} style={{ "--acc": ACC.blue }}
      className={`flex items-center gap-2 rounded-[10px] px-2 py-1.5 transition-colors ${mine ? "acc-card" : "hover:bg-x-raised"}`}>
      <span className={`w-7 shrink-0 text-center text-sm font-semibold tabular-nums ${r.rank <= 3 ? "text-x-gold-ink" : "text-x-mute"}`}>{r.rank}</span>
      <button type="button" onClick={() => onFly(r)} className="shrink-0 transition-transform hover:scale-110"><Face p={r} size={32} ring={row ? ACC.rose : "var(--x-line)"} /></button>
      <b className="flex min-w-0 flex-1 items-center gap-1.5 text-sm font-semibold">
        <span className="truncate">@{r.h}</span>
        {mine && <span className="acc-fill rounded-full px-1.5 text-[11px] font-semibold">You</span>}
        <Where className={`h-3.5 w-3.5 shrink-0 ${row ? "text-x-rose" : "text-x-mute"}`} aria-label={row ? "Row seat" : r.u ? "Uptown" : "Docks"} />
      </b>
      <span className={`text-sm font-semibold tabular-nums ${view === "week" ? "text-x-pos" : ""}`}>{view === "week" ? `+${fmt(r.wk)}` : fmt(r.e)}</span>
      <button type="button" onClick={() => onFly(r)} aria-label={`Fly to @${r.h}`} className="shrink-0 rounded-full p-1.5 text-x-mute transition-colors hover:bg-x-raised hover:text-x-ink"><MapPin className="h-4 w-4" /></button>
      {!mine && <Btn tone="green" className="shrink-0" disabled={Boolean(why)} title={why ?? "Trade"} data-testid="visit-button" onClick={(e) => onTrade(r, pointOf(e.currentTarget))}>{why === "Traded today" ? <Check className="h-4 w-4" /> : "Trade"}</Btn>}
    </li>
  );
}

export function Leaderboard({ town, user, econ, initial = "week", onFly, onTrade }) {
  const [view, setView] = useState(initial);
  const [q, setQ] = useState("");
  const key = VIEWS.find((v) => v[0] === view)[2];
  const ranked = useMemo(() => ladder(town.residents, key), [town.residents, key]);
  const term = q.trim().replace(/^@/, "").toLowerCase();
  const rows = term ? ranked.filter((r) => r.h.toLowerCase().includes(term)) : ranked.slice(0, SHOWN);
  const me = ranked.find((r) => r.k === user?.k);
  const extra = !term && me && me.rank > SHOWN ? me : null;
  const row = (r, i) => <Row key={r.k} r={r} i={i} view={view} mine={r.k === user?.k} why={tradeBlock(r, econ, user?.k)} onFly={onFly} onTrade={onTrade} />;

  return (
    <div className="px-3 pb-3 pt-2" data-testid="leaderboard">
      <div className="hud-well flex gap-0.5 rounded-full p-0.5" role="tablist">
        {VIEWS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => setView(id)} data-testid={`leaderboard-${id}`}
            className={`flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${view === id ? "bg-x-surface text-x-ink" : "text-x-mute hover:text-x-ink"}`}>{label}</button>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <label className="hud-well flex flex-1 items-center gap-2 rounded-full px-3 focus-within:ring-2 focus-within:ring-x-accent">
          <Search className="h-4 w-4 text-x-mute" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="@handle" data-testid="leaderboard-search" className="w-full bg-transparent py-2 text-sm outline-none placeholder:text-x-mute" />
        </label>
        <Info text={view === "week" ? "Weekly earnings. The top 10 move into Millionaires' Row on Monday." : "Worth: UXD plus everything invested in the dock."} />
      </div>
      <ul className="mt-2 space-y-1">
        {rows.map(row)}
        {extra && <><li className="py-0.5 text-center text-x-mute">···</li>{row(extra, 0)}</>}
        {!rows.length && <li className="py-8 text-center text-sm text-x-mute">No one by that name.</li>}
      </ul>
    </div>
  );
}
